import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, fileUrl, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  COMPLIANCE_LABEL, complianceClasses, categoryClasses, fmtDate, fmtDateTime, fmtSize,
  fmtMoney, monthLabel,
} from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Upload, FileText, Image as ImageIcon, Video, MapPin, ArrowLeft, Camera, Calendar,
  Crosshair, Download, Pencil, AlertTriangle, Scale, Landmark, Compass, Plus, Trash2, Gavel, Shield,
  Share2, Copy, FolderArchive, Clock, History, Send,
} from "lucide-react";
import { LocationFields, PoliceFields } from "@/components/LocationFields";
import { AuditList } from "@/pages/Activity";
import { CamerasPanel } from "@/components/CamerasPanel";

const CATEGORIES = ["Agricultural", "Commercial", "Residential"];
const FLAGGED = ["Encroachment suspected", "Boundary damaged", "Dispute notice found"];
const DIRECTIONS = ["East", "West", "North", "South", "North-East", "North-West", "South-East", "South-West"];
const SHAPES = ["Square", "Rectangle", "Irregular", "Triangular", "L-shaped"];
const RATINGS = ["Excellent", "Good", "Average", "Needs correction", "Not assessed"];
const VAASTU_DOC_TYPES = ["Compass / Orientation Screenshot", "Vaastu Report"];

const EMPTY_CASE = {
  case_number: "", court_name: "", case_type: "", our_role: "Respondent", opposing_party: "",
  filed_date: "", next_hearing_date: "", status: "Ongoing", advocate_name: "", advocate_firm: "",
  advocate_phone: "", advocate_email: "", advocate_fees: "", judgment_summary: "", outcome: "",
  notes: "", hearings: [],
};

const EMPTY_MORTGAGE = {
  bank_name: "", branch: "", loan_account_no: "", mortgage_type: "Equitable", sanctioned_amount: "",
  outstanding_amount: "", interest_rate: "", mortgage_date: "", maturity_date: "", release_date: "",
  status: "Active", contact_person: "", contact_phone: "", contact_email: "", notes: "",
};

export default function ParcelDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [parcel, setParcel] = useState(null);
  const [docs, setDocs] = useState([]);
  const [visits, setVisits] = useState([]);
  const [cases, setCases] = useState([]);
  const [mortgages, setMortgages] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [busy, setBusy] = useState(false);

  const [upOpen, setUpOpen] = useState(false);
  const [upForm, setUpForm] = useState({ doc_type: "Registration Paper", title: "", doc_date: "", reference_no: "", case_id: "", mortgage_id: "", valid_from: "", valid_until: "", reminder_days: 30, renewal_note: "" });
  const [file, setFile] = useState(null);
  const fileRef = useRef(null);

  const [editOpen, setEditOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [caseOpen, setCaseOpen] = useState(false);
  const [caseForm, setCaseForm] = useState(EMPTY_CASE);
  const [caseEditId, setCaseEditId] = useState(null);
  const [mortOpen, setMortOpen] = useState(false);
  const [mortForm, setMortForm] = useState(EMPTY_MORTGAGE);
  const [mortEditId, setMortEditId] = useState(null);
  const [shares, setShares] = useState([]);
  const [auditRows, setAuditRows] = useState([]);
  const [shareForm, setShareForm] = useState({
    label: "", days: "7", include_case_docs: false, include_mortgage_docs: false, include_visits: false,
    recipients: "", message: "",
  });
  const [emailShare, setEmailShare] = useState(null);
  const [emailForm, setEmailForm] = useState({ recipients: "", message: "" });
  const [appUrl, setAppUrl] = useState("");
  const [showRevoked, setShowRevoked] = useState(false);

  const shareUrl = (token) => `${appUrl || window.location.origin}/share/${token}`;

  const parseRecipients = (s) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

  const sendShareEmail = async (e) => {
    e.preventDefault();
    const recipients = parseRecipients(emailForm.recipients);
    if (!recipients.length) return toast.error("Add at least one email address");
    setBusy(true);
    try {
      const { data } = await api.post(`/shares/${emailShare.id}/email`, {
        recipients, message: emailForm.message,
      });
      toast.success(`Link emailed to ${data.recipients.length} recipient(s)`, {
        description: data.recipients.join(", "),
      });
      setEmailShare(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const load = async () => {
    const [p, d, v, c, m] = await Promise.all([
      api.get(`/parcels/${id}`),
      api.get(`/parcels/${id}/documents`),
      api.get(`/parcels/${id}/visits`),
      api.get(`/parcels/${id}/cases`),
      api.get(`/parcels/${id}/mortgages`),
    ]);
    setParcel(p.data); setDocs(d.data); setVisits(v.data); setCases(c.data); setMortgages(m.data);
    api.get(`/parcels/${id}/audit`).then(({ data }) => setAuditRows(data.rows)).catch(() => {});
    if (isAdmin) api.get(`/parcels/${id}/shares`).then(({ data }) => setShares(data)).catch(() => {});
  };

  const createShare = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/parcels/${id}/shares`, {
        ...shareForm, days: parseInt(shareForm.days, 10) || 7,
        recipients: parseRecipients(shareForm.recipients),
      });
      const url = shareUrl(data.token);
      const mailed = data.sends?.[0]?.recipients || [];
      try {
        await navigator.clipboard.writeText(url);
        toast.success(mailed.length ? `Link created and emailed to ${mailed.length}` : "Link created and copied",
          { description: mailed.length ? mailed.join(", ") : url });
      } catch {
        toast.success("Link created", { description: url });
      }
      setShareForm({ label: "", days: "7", include_case_docs: false, include_mortgage_docs: false, include_visits: false, recipients: "", message: "" });
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const revokeShare = async (sid) => {
    try {
      await api.delete(`/shares/${sid}`);
      toast.success("Link revoked");
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const copyShare = async (token) => {
    const url = shareUrl(token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied", { description: url });
    } catch {
      toast.info(url);
    }
  };

  useEffect(() => {
    load().catch(() => setParcel(false));
    api.get("/doc-types").then(({ data }) => setDocTypes(data)).catch(() => {});
    api.get("/config/public").then(({ data }) => setAppUrl(data.app_url)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setExecutives(data.filter((u) => u.role === "executive"))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const openUpload = (presetType = "Registration Paper", link = {}) => {
    setUpForm({ doc_type: presetType, title: "", doc_date: "", reference_no: "", case_id: "", mortgage_id: "", valid_from: "", valid_until: "", reminder_days: 30, renewal_note: "", ...link });
    setFile(null);
    setUpOpen(true);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Select a file first");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(upForm).forEach(([k, v]) => fd.append(k, v));
    try {
      await api.post(`/parcels/${id}/documents`, fd);
      toast.success("Document added");
      setUpOpen(false); setFile(null); load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const openEdit = () => {
    setEdit({
      name: parcel.name || "", survey_number: parcel.survey_number || "", village: parcel.village || "",
      mandal: parcel.mandal || "", district: parcel.district || "", state: parcel.state || "Telangana",
      extent_acres: parcel.extent_acres ?? "", asset_category: parcel.asset_category || "Agricultural",
      land_type: parcel.land_type || "", owner_name: parcel.owner_name || "", khata_number: parcel.khata_number || "",
      latitude: parcel.latitude ?? "", longitude: parcel.longitude ?? "", boundaries: parcel.boundaries || "",
      acquisition_date: parcel.acquisition_date || "", acquisition_value: parcel.acquisition_value ?? "",
      status: parcel.status || "Active", notes: parcel.notes || "",
      assigned_executive_id: parcel.assigned_executive_id || "",
      visit_frequency: parcel.visit_frequency || "monthly",
      has_litigation: !!parcel.has_litigation, has_mortgage: !!parcel.has_mortgage,
      vaastu_facing: parcel.vaastu_facing || "", vaastu_shape: parcel.vaastu_shape || "",
      vaastu_slope: parcel.vaastu_slope || "", vaastu_water_source: parcel.vaastu_water_source || "",
      vaastu_road_position: parcel.vaastu_road_position || "",
      vaastu_rating: parcel.vaastu_rating || "Not assessed", vaastu_notes: parcel.vaastu_notes || "",
      pincode: parcel.pincode || "", police_station: parcel.police_station || "",
      police_station_phone: parcel.police_station_phone || "",
      police_station_address: parcel.police_station_address || "",
      police_jurisdiction_notes: parcel.police_jurisdiction_notes || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/parcels/${id}`, {
        ...edit,
        extent_acres: parseFloat(edit.extent_acres) || 0,
        latitude: edit.latitude === "" ? null : parseFloat(edit.latitude),
        longitude: edit.longitude === "" ? null : parseFloat(edit.longitude),
        acquisition_value: edit.acquisition_value === "" ? null : parseFloat(edit.acquisition_value),
        acquisition_date: edit.acquisition_date || null,
        assigned_executive_id: edit.assigned_executive_id || null,
      });
      toast.success("Asset updated");
      setEditOpen(false); load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const saveCase = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...caseForm,
      advocate_fees: caseForm.advocate_fees === "" ? null : parseFloat(caseForm.advocate_fees),
      filed_date: caseForm.filed_date || null,
      next_hearing_date: caseForm.next_hearing_date || null,
      hearings: caseForm.hearings.filter((h) => h.hearing_date),
    };
    try {
      if (caseEditId) await api.put(`/cases/${caseEditId}`, payload);
      else await api.post(`/parcels/${id}/cases`, payload);
      toast.success(caseEditId ? "Case updated" : "Case added");
      setCaseOpen(false); setCaseEditId(null); setCaseForm(EMPTY_CASE); load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const saveMortgage = async (e) => {
    e.preventDefault();
    setBusy(true);
    const num = (v) => (v === "" ? null : parseFloat(v));
    const payload = {
      ...mortForm,
      sanctioned_amount: num(mortForm.sanctioned_amount),
      outstanding_amount: num(mortForm.outstanding_amount),
      interest_rate: num(mortForm.interest_rate),
      mortgage_date: mortForm.mortgage_date || null,
      maturity_date: mortForm.maturity_date || null,
      release_date: mortForm.release_date || null,
    };
    try {
      if (mortEditId) await api.put(`/mortgages/${mortEditId}`, payload);
      else await api.post(`/parcels/${id}/mortgages`, payload);
      toast.success(mortEditId ? "Mortgage updated" : "Mortgage added");
      setMortOpen(false); setMortEditId(null); setMortForm(EMPTY_MORTGAGE); load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const removeCase = async (cid) => {
    await api.delete(`/cases/${cid}`); toast.success("Case removed"); load();
  };
  const removeMortgage = async (mid) => {
    await api.delete(`/mortgages/${mid}`); toast.success("Mortgage removed"); load();
  };

  if (parcel === false) return <div className="p-10 text-sm text-muted-foreground">Asset not found or not accessible.</div>;
  if (!parcel) return <div className="p-10 text-sm text-muted-foreground">Loading asset…</div>;

  const grouped = visits.reduce((acc, v) => {
    (acc[v.period] = acc[v.period] || []).push(v);
    return acc;
  }, {});
  const canCapture = isAdmin || parcel.assigned_executive_id === user.id;
  const repoDocs = docs.filter((d) => !VAASTU_DOC_TYPES.includes(d.doc_type));
  const vaastuDocs = docs.filter((d) => VAASTU_DOC_TYPES.includes(d.doc_type));
  const docsFor = (key, val) => docs.filter((d) => d[key] === val);

  const inp = (state, setState, label, key, props = {}, testid) => (
    <div>
      <Label className="label-tiny">{label}</Label>
      <Input className="mt-2" data-testid={testid} value={state[key] ?? ""}
        onChange={(e) => setState({ ...state, [key]: e.target.value })} {...props} />
    </div>
  );

  const sel = (state, setState, label, key, options, testid) => (
    <div>
      <Label className="label-tiny">{label}</Label>
      <Select value={state[key] || "none"} onValueChange={(v) => setState({ ...state, [key]: v === "none" ? "" : v })}>
        <SelectTrigger className="mt-2" data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Not specified</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  const DocChips = ({ list }) => (
    <div className="mt-3 flex flex-wrap gap-2">
      {list.length === 0 && <span className="text-xs text-muted-foreground">No documents attached yet.</span>}
      {list.map((d) => (
        <a key={d.id} href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer"
          data-testid={`linked-doc-${d.id}`}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-muted/50 px-2.5 py-1.5 text-xs hover:border-primary hover:text-primary transition-colors duration-200">
          <FileText size={12} /> {d.title || d.original_filename}
        </a>
      ))}
    </div>
  );

  return (
    <div className="p-5 md:p-10 max-w-[1400px] rise" data-testid="parcel-detail-page">
      <Link to="/parcels" data-testid="back-to-parcels"
        className="inline-flex items-center gap-2 label-tiny hover:text-primary transition-colors duration-200">
        <ArrowLeft size={13} /> Asset register
      </Link>

      <div className="mt-5 flex flex-wrap items-start justify-between gap-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className={`rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${categoryClasses(parcel.asset_category)}`}>
              {parcel.asset_category || "Agricultural"}
            </span>
            <span className={`rounded-md border px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${complianceClasses(parcel.compliance)}`}
              data-testid="parcel-compliance-badge">
              {COMPLIANCE_LABEL[parcel.compliance]}
            </span>
            {parcel.open_case_count > 0 && (
              <span className="rounded-md border border-red-200 bg-red-50 text-red-700 px-2 py-1 text-[10px] font-bold uppercase tracking-wider"
                data-testid="badge-litigation">
                {parcel.open_case_count} open case(s)
              </span>
            )}
            {parcel.active_mortgage_count > 0 && (
              <span className="rounded-md border border-cyan-200 bg-cyan-50 text-cyan-700 px-2 py-1 text-[10px] font-bold uppercase tracking-wider"
                data-testid="badge-mortgage">
                mortgaged
              </span>
            )}
          </div>
          <h1 className="mt-3 font-display text-3xl md:text-4xl font-bold tracking-tighter" data-testid="parcel-title">
            {parcel.name}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground flex items-center gap-1.5">
            <MapPin size={14} /> {[parcel.village, parcel.mandal, parcel.district, parcel.state].filter(Boolean).join(", ")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <a href={`${API}/export/parcel/${id}/backup.zip?include_media=true&auth=${localStorage.getItem("token")}`}
              data-testid="parcel-backup-link"
              className="inline-flex items-center gap-2 text-sm font-medium rounded-lg border border-border px-4 py-2.5 bg-card hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-200">
              <FolderArchive size={15} /> Backup ZIP
            </a>
          )}
          {isAdmin && (
            <Button variant="outline" onClick={openEdit} data-testid="edit-parcel-button" className="rounded-lg">
              <Pencil size={15} className="mr-2" /> Edit asset
            </Button>
          )}
          {canCapture && (
            <Link to={`/parcels/${id}/capture`} data-testid="capture-visit-link">
              <Button className="rounded-lg active:scale-[0.98] transition-transform duration-150">
                <Camera size={16} className="mr-2" /> Record field visit
              </Button>
            </Link>
          )}
        </div>
      </div>

      <div className="mt-8 surface grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-y md:divide-y-0 divide-border overflow-hidden">
        {[
          ["Survey no.", parcel.survey_number || "—"],
          ["Extent", `${parcel.extent_acres || 0} ac`],
          ["Khata / Passbook", parcel.khata_number || "—"],
          ["Owner", parcel.owner_name || "—"],
          ["Documents", parcel.document_count],
          ["Next hearing", parcel.next_hearing_date ? fmtDate(parcel.next_hearing_date) : "—"],
        ].map(([l, v]) => (
          <div key={l} className="p-5">
            <p className="label-tiny">{l}</p>
            <p className="mt-2 text-sm font-semibold break-words">{v}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="timeline" className="mt-8">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="litigation" data-testid="tab-litigation">Litigation {cases.length > 0 ? `(${cases.length})` : ""}</TabsTrigger>
          <TabsTrigger value="mortgages" data-testid="tab-mortgages">Mortgages {mortgages.length > 0 ? `(${mortgages.length})` : ""}</TabsTrigger>
          <TabsTrigger value="vaastu" data-testid="tab-vaastu">Vaastu</TabsTrigger>
          <TabsTrigger value="cameras" data-testid="tab-cameras">Live CCTV</TabsTrigger>
          {isAdmin && <TabsTrigger value="sharing" data-testid="tab-sharing">Sharing</TabsTrigger>}
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="activity" data-testid="tab-activity">Activity</TabsTrigger>
        </TabsList>

        {/* TIMELINE */}
        <TabsContent value="timeline" className="mt-6">
          {visits.length === 0 ? (
            <div className="surface p-12 text-center" data-testid="timeline-empty">
              <p className="font-display text-xl font-bold tracking-tight">No field captures yet</p>
              <p className="mt-2 text-sm text-muted-foreground">
                The assigned executive's geotagged photos and videos will build the timeline here.
              </p>
            </div>
          ) : (
            <div className="space-y-10" data-testid="visit-timeline">
              {Object.entries(grouped).map(([period, list]) => (
                <div key={period}>
                  <div className="flex items-center gap-3">
                    <Calendar size={15} className="text-primary" />
                    <p className="label-tiny">{monthLabel(period)}</p>
                    <span className="text-xs text-muted-foreground font-mono">{list.length} visit(s)</span>
                  </div>
                  <div className="mt-4 border-l-2 border-border pl-5 md:pl-8 space-y-4">
                    {list.map((v) => (
                      <div key={v.id} data-testid={`visit-${v.id}`} className="relative surface p-5">
                        <span className="absolute -left-[27px] md:-left-[39px] top-6 h-3 w-3 rounded-full bg-primary ring-4 ring-background" />
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold">{fmtDateTime(v.captured_at)}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              By {v.executive_name} · Observation:{" "}
                              {FLAGGED.includes(v.observation) ? (
                                <span className="inline-flex items-center gap-1 text-destructive font-semibold"
                                  data-testid={`visit-flag-${v.id}`}>
                                  <AlertTriangle size={12} /> {v.observation} — admins alerted
                                </span>
                              ) : v.observation}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="label-tiny flex items-center gap-1 justify-end"><Crosshair size={11} /> Geotag</p>
                            <p className="text-xs font-mono mt-1" data-testid={`visit-geo-${v.id}`}>
                              {v.latitude != null ? `${v.latitude.toFixed(5)}, ${v.longitude.toFixed(5)}` : "Not captured"}
                            </p>
                            {v.latitude != null && (
                              <a href={`https://www.google.com/maps?q=${v.latitude},${v.longitude}`}
                                target="_blank" rel="noreferrer" className="text-[11px] text-primary hover:underline">
                                View on map
                              </a>
                            )}
                          </div>
                        </div>
                        {v.notes && <p className="mt-3 text-sm leading-relaxed">{v.notes}</p>}
                        {v.media?.length > 0 && (
                          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                            {v.media.map((m) => (
                              <a key={m.id} href={fileUrl(m.storage_path)} target="_blank" rel="noreferrer"
                                data-testid={`media-${m.id}`}
                                className="block rounded-lg border border-border overflow-hidden group">
                                {m.kind === "video" ? (
                                  <div className="aspect-[4/3] bg-zinc-900 flex flex-col items-center justify-center text-white gap-1">
                                    <Video size={22} />
                                    <span className="text-[10px] uppercase tracking-widest">Video</span>
                                  </div>
                                ) : (
                                  <img src={fileUrl(m.storage_path)} alt={m.original_filename} loading="lazy"
                                    className="aspect-[4/3] w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                                )}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* DOCUMENTS */}
        <TabsContent value="documents" className="mt-6">
          <div className="flex justify-end">
            <Button data-testid="upload-document-button" onClick={() => openUpload()} className="rounded-lg">
              <Upload size={16} className="mr-2" /> Upload document
            </Button>
          </div>
          {repoDocs.length === 0 ? (
            <div className="mt-6 surface p-12 text-center" data-testid="documents-empty">
              <p className="font-display text-xl font-bold tracking-tight">Repository is empty</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Upload registration papers, pahani/adangal, EC, legal opinion, survey drawings and more.
              </p>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="documents-grid">
              {repoDocs.map((d) => {
                const Icon = d.content_type?.startsWith("image") ? ImageIcon : FileText;
                return (
                  <a key={d.id} href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer"
                    data-testid={`document-card-${d.id}`}
                    className="surface p-5 flex gap-4 hover:-translate-y-1 hover:shadow-md hover:border-primary/40 transition-[transform,box-shadow,border-color] duration-200">
                    <Icon size={22} className="text-primary shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <p className="label-tiny">{d.doc_type}</p>
                      <p className="mt-1.5 text-sm font-semibold truncate">{d.title}</p>
                      <p className="mt-1 text-xs text-muted-foreground truncate">
                        {d.doc_date ? `${fmtDate(d.doc_date)} · ` : ""}{fmtSize(d.size)}
                      </p>
                      {d.reference_no && (
                        <p className="text-xs font-mono text-muted-foreground mt-0.5">Ref {d.reference_no}</p>
                      )}
                      {d.expiry?.state && d.expiry.state !== "none" && (
                        <span data-testid={`doc-expiry-${d.id}`}
                          className={`mt-2 inline-block rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                            d.expiry.state === "expired" ? "bg-red-50 text-red-700 border-red-200"
                              : d.expiry.state === "expiring" ? "bg-amber-50 text-amber-700 border-amber-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                          {d.expiry.state === "expired"
                            ? `Lapsed ${Math.abs(d.expiry.days_left)}d ago`
                            : d.expiry.state === "expiring"
                            ? `${d.expiry.days_left}d left · till ${fmtDate(d.valid_until)}`
                            : `Valid till ${fmtDate(d.valid_until)}`}
                        </span>
                      )}
                    </div>
                    <Download size={15} className="text-muted-foreground shrink-0" />
                  </a>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* LITIGATION */}
        <TabsContent value="litigation" className="mt-6">
          {isAdmin && (
            <div className="flex justify-end">
              <Button data-testid="add-case-button" className="rounded-lg"
                onClick={() => { setCaseForm(EMPTY_CASE); setCaseEditId(null); setCaseOpen(true); }}>
                <Plus size={16} className="mr-2" /> Add court case
              </Button>
            </div>
          )}
          {cases.length === 0 ? (
            <div className="mt-6 surface p-12 text-center" data-testid="cases-empty">
              <Scale size={26} className="mx-auto text-muted-foreground" />
              <p className="mt-4 font-display text-xl font-bold tracking-tight">No litigation recorded</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Add court cases with advocate details, hearing dates and judgments if this asset is under dispute.
              </p>
            </div>
          ) : (
            <div className="mt-6 space-y-4" data-testid="cases-list">
              {cases.map((c) => (
                <div key={c.id} className="surface p-6" data-testid={`case-card-${c.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="label-tiny">{c.case_type || "Case"}</span>
                        <span className={`rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          c.status === "Closed" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-red-50 text-red-700 border-red-200"}`}>
                          {c.status}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                          We are {c.our_role}
                        </span>
                      </div>
                      <p className="mt-2 font-display text-lg font-bold tracking-tight">{c.case_number}</p>
                      <p className="text-sm text-muted-foreground">{c.court_name || "Court not specified"}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      {c.next_hearing_date && (
                        <span className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700"
                          data-testid={`case-hearing-${c.id}`}>
                          <Gavel size={13} /> Next hearing {fmtDate(c.next_hearing_date)}
                        </span>
                      )}
                      {isAdmin && (
                        <>
                          <button data-testid={`edit-case-${c.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-200"
                            onClick={() => {
                              setCaseForm({ ...EMPTY_CASE, ...c, advocate_fees: c.advocate_fees ?? "", filed_date: c.filed_date || "", next_hearing_date: c.next_hearing_date || "", hearings: c.hearings || [] });
                              setCaseEditId(c.id); setCaseOpen(true);
                            }}>
                            <Pencil size={15} />
                          </button>
                          <button data-testid={`delete-case-${c.id}`} onClick={() => removeCase(c.id)}
                            className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                            <Trash2 size={15} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {[
                      ["Opposing party", c.opposing_party],
                      ["Filed on", c.filed_date ? fmtDate(c.filed_date) : ""],
                      ["Advocate", c.advocate_name],
                      ["Advocate firm", c.advocate_firm],
                      ["Advocate phone", c.advocate_phone],
                      ["Advocate email", c.advocate_email],
                      ["Advocate fees", c.advocate_fees ? fmtMoney(c.advocate_fees) : ""],
                      ["Outcome", c.outcome],
                    ].map(([l, v]) => (
                      <div key={l}>
                        <p className="label-tiny">{l}</p>
                        <p className="mt-1 text-sm break-words">{v || "—"}</p>
                      </div>
                    ))}
                  </div>

                  {c.hearings?.length > 0 && (
                    <div className="mt-5">
                      <p className="label-tiny">Hearing history</p>
                      <div className="mt-2 divide-y divide-border">
                        {c.hearings.map((h, i) => (
                          <div key={i} className="py-2 flex flex-wrap gap-3 justify-between text-sm">
                            <span className="font-mono text-xs">{fmtDate(h.hearing_date)}</span>
                            <span className="flex-1 min-w-[140px]">{h.purpose || "—"}</span>
                            <span className="text-muted-foreground text-xs">{h.outcome || "—"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {c.judgment_summary && (
                    <div className="mt-5 rounded-lg bg-muted/60 p-4">
                      <p className="label-tiny">Judgment / order summary</p>
                      <p className="mt-1.5 text-sm leading-relaxed">{c.judgment_summary}</p>
                    </div>
                  )}
                  {c.notes && <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{c.notes}</p>}

                  <div className="mt-5 border-t border-border pt-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="label-tiny">Case documents</p>
                      <Button variant="outline" size="sm" className="rounded-lg" data-testid={`upload-case-doc-${c.id}`}
                        onClick={() => openUpload("Court Order / Interim Order", { case_id: c.id })}>
                        <Upload size={13} className="mr-1.5" /> Attach
                      </Button>
                    </div>
                    <DocChips list={docsFor("case_id", c.id)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* MORTGAGES */}
        <TabsContent value="mortgages" className="mt-6">
          {isAdmin && (
            <div className="flex justify-end">
              <Button data-testid="add-mortgage-button" className="rounded-lg"
                onClick={() => { setMortForm(EMPTY_MORTGAGE); setMortEditId(null); setMortOpen(true); }}>
                <Plus size={16} className="mr-2" /> Add mortgage
              </Button>
            </div>
          )}
          {mortgages.length === 0 ? (
            <div className="mt-6 surface p-12 text-center" data-testid="mortgages-empty">
              <Landmark size={26} className="mx-auto text-muted-foreground" />
              <p className="mt-4 font-display text-xl font-bold tracking-tight">No mortgage recorded</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Record the bank, loan account and amounts, then attach the mortgage deed and bank letters.
              </p>
            </div>
          ) : (
            <div className="mt-6 space-y-4" data-testid="mortgages-list">
              {mortgages.map((m) => (
                <div key={m.id} className="surface p-6" data-testid={`mortgage-card-${m.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="label-tiny">{m.mortgage_type} mortgage</span>
                        <span className={`rounded-md border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                          m.status === "Released" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                            : "bg-cyan-50 text-cyan-700 border-cyan-200"}`}>
                          {m.status}
                        </span>
                      </div>
                      <p className="mt-2 font-display text-lg font-bold tracking-tight">{m.bank_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {[m.branch, m.loan_account_no && `A/c ${m.loan_account_no}`].filter(Boolean).join(" · ") || "—"}
                      </p>
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-3">
                        <button data-testid={`edit-mortgage-${m.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-200"
                          onClick={() => {
                            setMortForm({ ...EMPTY_MORTGAGE, ...m,
                              sanctioned_amount: m.sanctioned_amount ?? "", outstanding_amount: m.outstanding_amount ?? "",
                              interest_rate: m.interest_rate ?? "", mortgage_date: m.mortgage_date || "",
                              maturity_date: m.maturity_date || "", release_date: m.release_date || "" });
                            setMortEditId(m.id); setMortOpen(true);
                          }}>
                          <Pencil size={15} />
                        </button>
                        <button data-testid={`delete-mortgage-${m.id}`} onClick={() => removeMortgage(m.id)}
                          className="text-muted-foreground hover:text-destructive transition-colors duration-200">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {[
                      ["Sanctioned", fmtMoney(m.sanctioned_amount)],
                      ["Outstanding", fmtMoney(m.outstanding_amount)],
                      ["Interest rate", m.interest_rate ? `${m.interest_rate}%` : "—"],
                      ["Mortgaged on", m.mortgage_date ? fmtDate(m.mortgage_date) : "—"],
                      ["Maturity", m.maturity_date ? fmtDate(m.maturity_date) : "—"],
                      ["Released on", m.release_date ? fmtDate(m.release_date) : "—"],
                      ["Bank contact", m.contact_person || "—"],
                      ["Contact phone", m.contact_phone || "—"],
                    ].map(([l, v]) => (
                      <div key={l}>
                        <p className="label-tiny">{l}</p>
                        <p className="mt-1 text-sm break-words">{v}</p>
                      </div>
                    ))}
                  </div>
                  {m.notes && <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{m.notes}</p>}

                  <div className="mt-5 border-t border-border pt-4">
                    <div className="flex items-center justify-between gap-3">
                      <p className="label-tiny">Mortgage documents</p>
                      <Button variant="outline" size="sm" className="rounded-lg" data-testid={`upload-mortgage-doc-${m.id}`}
                        onClick={() => openUpload("Bank Mortgage Deed", { mortgage_id: m.id })}>
                        <Upload size={13} className="mr-1.5" /> Attach
                      </Button>
                    </div>
                    <DocChips list={docsFor("mortgage_id", m.id)} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* VAASTU */}
        <TabsContent value="vaastu" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2 surface p-6">
              <p className="label-tiny flex items-center gap-2"><Compass size={14} className="text-primary" /> Orientation</p>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-5">
                {[
                  ["Facing", parcel.vaastu_facing],
                  ["Plot shape", parcel.vaastu_shape],
                  ["Road position", parcel.vaastu_road_position],
                  ["Slope towards", parcel.vaastu_slope],
                  ["Water source", parcel.vaastu_water_source],
                  ["Rating", parcel.vaastu_rating],
                ].map(([l, v]) => (
                  <div key={l} data-testid={`vaastu-${l.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                    <p className="label-tiny">{l}</p>
                    <p className="mt-1 text-sm font-semibold">{v || "—"}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6">
                <p className="label-tiny">Vaastu notes</p>
                <p className="mt-1.5 text-sm leading-relaxed whitespace-pre-line" data-testid="vaastu-notes">
                  {parcel.vaastu_notes || "No vaastu notes captured yet."}
                </p>
              </div>
              {isAdmin && (
                <Button variant="outline" onClick={openEdit} className="mt-6 rounded-lg" data-testid="edit-vaastu-button">
                  <Pencil size={14} className="mr-2" /> Edit vaastu details
                </Button>
              )}
            </div>

            <div className="surface p-6">
              <div className="flex items-center justify-between gap-3">
                <p className="label-tiny">Compass screenshots</p>
                <Button variant="outline" size="sm" className="rounded-lg" data-testid="upload-compass-button"
                  onClick={() => openUpload("Compass / Orientation Screenshot")}>
                  <Upload size={13} className="mr-1.5" /> Upload
                </Button>
              </div>
              {vaastuDocs.length === 0 ? (
                <p className="mt-4 text-xs text-muted-foreground" data-testid="compass-empty">
                  Upload compass app screenshots and vaastu reports captured at the site.
                </p>
              ) : (
                <div className="mt-4 grid grid-cols-2 gap-3" data-testid="compass-grid">
                  {vaastuDocs.map((d) => (
                    <a key={d.id} href={fileUrl(d.storage_path)} target="_blank" rel="noreferrer"
                      data-testid={`compass-doc-${d.id}`}
                      className="rounded-lg border border-border overflow-hidden group">
                      {d.content_type?.startsWith("image") ? (
                        <img src={fileUrl(d.storage_path)} alt={d.title} loading="lazy"
                          className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      ) : (
                        <div className="aspect-square flex flex-col items-center justify-center gap-2 bg-muted">
                          <FileText size={20} className="text-muted-foreground" />
                          <span className="text-[10px] px-2 text-center truncate w-full">{d.title}</span>
                        </div>
                      )}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* SHARING */}
        {isAdmin && (
          <TabsContent value="sharing" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="surface p-6">
                <p className="label-tiny flex items-center gap-2"><Share2 size={14} className="text-primary" /> New read-only link</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  Share this property's documents with a bank or advocate. No login needed and the link
                  expires by itself.
                </p>
                <form onSubmit={createShare} className="mt-5 space-y-4" data-testid="share-form">
                  <div>
                    <Label className="label-tiny">Shared with</Label>
                    <Input className="mt-2" data-testid="share-label-input" value={shareForm.label}
                      onChange={(e) => setShareForm({ ...shareForm, label: e.target.value })}
                      placeholder="SBI Shamirpet — loan appraisal" />
                  </div>
                  <div>
                    <Label className="label-tiny">Valid for</Label>
                    <Select value={shareForm.days} onValueChange={(v) => setShareForm({ ...shareForm, days: v })}>
                      <SelectTrigger className="mt-2" data-testid="share-days-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[["3", "3 days"], ["7", "7 days"], ["14", "14 days"], ["30", "30 days"]].map(([v, l]) => (
                          <SelectItem key={v} value={v}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-3">
                    {[
                      ["include_case_docs", "Include litigation papers"],
                      ["include_mortgage_docs", "Include mortgage papers"],
                      ["include_visits", "Include recent site inspections"],
                    ].map(([key, label]) => (
                      <label key={key} className="flex items-center justify-between gap-3 text-sm">
                        {label}
                        <Switch checked={shareForm[key]} data-testid={`share-${key}-switch`}
                          onCheckedChange={(v) => setShareForm({ ...shareForm, [key]: v })} />
                      </label>
                    ))}
                  </div>
                  <div>
                    <Label className="label-tiny">Email the link to (optional)</Label>
                    <Input className="mt-2" data-testid="share-recipients-input"
                      value={shareForm.recipients}
                      onChange={(e) => setShareForm({ ...shareForm, recipients: e.target.value })}
                      placeholder="manager@sbi.co.in, advocate@firm.in" />
                    <p className="mt-1.5 text-xs text-muted-foreground">
                      Comma separated. Every send is recorded against the link.
                    </p>
                  </div>
                  <div>
                    <Label className="label-tiny">Message (optional)</Label>
                    <Textarea className="mt-2" rows={3} data-testid="share-message-input"
                      value={shareForm.message}
                      onChange={(e) => setShareForm({ ...shareForm, message: e.target.value })}
                      placeholder="Sharing the title documents for the loan appraisal." />
                  </div>
                  <Button type="submit" disabled={busy} data-testid="share-submit-button" className="w-full rounded-lg">
                    <Share2 size={15} className="mr-2" /> {busy ? "Creating…" : "Create link"}
                  </Button>
                </form>
              </div>

              <div className="lg:col-span-2 surface p-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="label-tiny">Active links</p>
                  {shares.some((s) => s.revoked || s.expired) && (
                    <button type="button" data-testid="toggle-revoked-shares"
                      onClick={() => setShowRevoked(!showRevoked)}
                      className="text-xs font-semibold text-primary hover:underline">
                      {showRevoked ? "Hide" : "Show"} expired &amp; revoked
                      {` (${shares.filter((s) => s.revoked || s.expired).length})`}
                    </button>
                  )}
                </div>
                {(showRevoked ? shares : shares.filter((s) => !(s.revoked || s.expired))).length === 0 ? (
                  <p className="mt-5 text-sm text-muted-foreground" data-testid="shares-empty">
                    No active links for this property.
                  </p>
                ) : (
                  <div className="mt-4 divide-y divide-border" data-testid="shares-list">
                    {(showRevoked ? shares : shares.filter((s) => !(s.revoked || s.expired))).map((s) => {
                      const dead = s.revoked || s.expired;
                      return (
                        <div key={s.id} className="py-4 flex flex-wrap items-start justify-between gap-3"
                          data-testid={`share-row-${s.id}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold truncate">
                              {s.label || "Untitled share"}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                              <Clock size={12} />
                              {s.revoked ? "Revoked" : s.expired ? "Expired" : `Valid until ${fmtDate(s.expires_at)}`}
                              {" · "}{s.view_count} view(s)
                              {s.last_viewed_at ? ` · last opened ${fmtDate(s.last_viewed_at)}` : ""}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {[s.include_case_docs && "litigation", s.include_mortgage_docs && "mortgage",
                                s.include_visits && "inspections"].filter(Boolean).join(", ") || "documents only"}
                            </p>
                            {s.sends?.length > 0 && (
                              <div className="mt-2 space-y-1" data-testid={`share-sends-${s.id}`}>
                                {(s.sends.length > 2 ? s.sends.slice(-2) : s.sends).map((snd, i) => (
                                  <p key={i} className="text-xs text-muted-foreground flex items-start gap-1.5">
                                    <Send size={11} className="mt-0.5 shrink-0 text-primary" />
                                    <span>
                                      {snd.recipients?.join(", ") || "delivery failed"}
                                      <span className="text-muted-foreground/70">
                                        {" "}· {fmtDate(snd.at)} by {snd.by_name}
                                      </span>
                                    </span>
                                  </p>
                                ))}
                                {s.sends.length > 2 && (
                                  <p className="text-xs text-muted-foreground/70 pl-4">
                                    + {s.sends.length - 2} earlier send(s) — see the Activity tab
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {!dead && (
                              <>
                                <Button variant="outline" size="sm" className="rounded-lg"
                                  data-testid={`copy-share-${s.id}`} onClick={() => copyShare(s.token)}>
                                  <Copy size={13} className="mr-1.5" /> Copy
                                </Button>
                                <Button variant="outline" size="sm" className="rounded-lg"
                                  data-testid={`email-share-${s.id}`}
                                  onClick={() => { setEmailShare(s); setEmailForm({ recipients: "", message: "" }); }}>
                                  <Send size={13} className="mr-1.5" /> Email
                                </Button>
                                <a href={`/share/${s.token}`} target="_blank" rel="noreferrer"
                                  data-testid={`open-share-${s.id}`}
                                  className="text-xs font-semibold text-primary hover:underline px-2">
                                  Open
                                </a>
                              </>
                            )}
                            <button data-testid={`revoke-share-${s.id}`} disabled={dead}
                              onClick={() => revokeShare(s.id)}
                              className="text-muted-foreground hover:text-destructive disabled:opacity-30 transition-colors duration-200">
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        )}

        {/* DETAILS */}
        <TabsContent value="details" className="mt-6">
          <div className="surface p-6 mb-4">
            <p className="label-tiny flex items-center gap-2"><Shield size={14} className="text-primary" /> Police station jurisdiction</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                ["Police station", parcel.police_station],
                ["PS phone", parcel.police_station_phone],
                ["Pincode", parcel.pincode],
                ["Jurisdiction notes", parcel.police_jurisdiction_notes],
              ].map(([l, v]) => (
                <div key={l} data-testid={`ps-${l.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  <p className="label-tiny">{l}</p>
                  <p className="mt-1 text-sm break-words">{v || "—"}</p>
                </div>
              ))}
              <div className="sm:col-span-2 lg:col-span-4">
                <p className="label-tiny">PS address</p>
                <p className="mt-1 text-sm">{parcel.police_station_address || "—"}</p>
              </div>
            </div>
          </div>
          <div className="surface p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              ["Category", parcel.asset_category],
              ["Sub type", parcel.land_type],
              ["Status", parcel.status],
              ["Visit frequency", parcel.visit_frequency],
              ["Assigned executive", parcel.assigned_executive?.name || "Unassigned"],
              ["Acquisition date", parcel.acquisition_date ? fmtDate(parcel.acquisition_date) : "—"],
              ["Acquisition value", fmtMoney(parcel.acquisition_value)],
              ["Coordinates", parcel.latitude != null ? `${parcel.latitude}, ${parcel.longitude}` : "—"],
              ["Litigation flag", parcel.has_litigation ? "Yes" : "No"],
              ["Mortgage flag", parcel.has_mortgage ? "Yes" : "No"],
            ].map(([l, v]) => (
              <div key={l}>
                <p className="label-tiny">{l}</p>
                <p className="mt-1.5 text-sm">{v || "—"}</p>
              </div>
            ))}
            {parcel.notes && (
              <div className="sm:col-span-2 lg:col-span-3">
                <p className="label-tiny">Notes</p>
                <p className="mt-1.5 text-sm leading-relaxed">{parcel.notes}</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ACTIVITY */}
        <TabsContent value="cameras" className="mt-6">
          <CamerasPanel parcelId={id} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="activity" className="mt-6">
          <div className="surface p-6" data-testid="parcel-activity">
            <div className="flex items-center justify-between gap-3">
              <p className="label-tiny flex items-center gap-2">
                <History size={14} className="text-primary" /> Change history for this property
              </p>
              {isAdmin && (
                <Link to="/activity" className="text-xs font-semibold text-primary hover:underline"
                  data-testid="full-audit-link">
                  Portfolio audit trail
                </Link>
              )}
            </div>
            <div className="mt-4">
              <AuditList rows={auditRows} showParcel={false} />
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* UPLOAD DOC */}
      <Dialog open={upOpen} onOpenChange={setUpOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display font-bold tracking-tight">Add document</DialogTitle>
            <DialogDescription>
              Title deeds, pahani, court orders, bank letters, compass screenshots and more.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={upload} className="space-y-4" data-testid="document-form">
            <div>
              <Label className="label-tiny">Document type</Label>
              <Select value={upForm.doc_type} onValueChange={(v) => setUpForm({ ...upForm, doc_type: v })}>
                <SelectTrigger className="mt-2" data-testid="doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {docTypes.map((t) => <SelectItem key={t} value={t} data-testid={`doc-type-${t}`}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {inp(upForm, setUpForm, "Title", "title", { placeholder: "Sale deed 2019 — Sy 123/4A" }, "doc-title-input")}
            <div className="grid grid-cols-2 gap-3">
              {inp(upForm, setUpForm, "Document date", "doc_date", { type: "date" }, "doc-date-input")}
              {inp(upForm, setUpForm, "Reference no.", "reference_no", {}, "doc-ref-input")}
            </div>
            <div className="rounded-lg border border-border bg-muted/40 p-4">
              <p className="label-tiny">Validity (optional)</p>
              <p className="mt-1.5 text-xs text-muted-foreground">
                For leases, tax receipts, ECs, insurance and NOCs — REAM will warn you before the
                date lapses.
              </p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {inp(upForm, setUpForm, "Valid from", "valid_from", { type: "date" }, "doc-valid-from-input")}
                {inp(upForm, setUpForm, "Valid until", "valid_until", { type: "date" }, "doc-valid-until-input")}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                {inp(upForm, setUpForm, "Remind me (days before)", "reminder_days", { type: "number", min: "1" }, "doc-reminder-days-input")}
                {inp(upForm, setUpForm, "Renewal note", "renewal_note", { placeholder: "Renew with MRO office" }, "doc-renewal-note-input")}
              </div>
            </div>
            <div>
              <Label className="label-tiny">File</Label>
              <input ref={fileRef} type="file" data-testid="doc-file-input"
                onChange={(e) => setFile(e.target.files[0])}
                className="mt-2 block w-full text-sm rounded-lg border border-border bg-card p-2" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="doc-submit-button" className="rounded-lg">
                {busy ? "Uploading…" : "Upload"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* SHARE EMAIL DIALOG */}
      <Dialog open={!!emailShare} onOpenChange={(v) => !v && setEmailShare(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display font-bold tracking-tight">Email this link</DialogTitle>
            <DialogDescription>
              {emailShare?.label || "Read-only link"} · valid until {fmtDate(emailShare?.expires_at)}.
              Recipients are recorded against the link and in the audit trail.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={sendShareEmail} className="space-y-4" data-testid="share-email-form">
            <div>
              <Label className="label-tiny">Send to</Label>
              <Input className="mt-2" required data-testid="share-email-recipients-input"
                value={emailForm.recipients}
                onChange={(e) => setEmailForm({ ...emailForm, recipients: e.target.value })}
                placeholder="manager@sbi.co.in, advocate@firm.in" />
              <p className="mt-1.5 text-xs text-muted-foreground">Comma separated, up to 10 addresses.</p>
            </div>
            <div>
              <Label className="label-tiny">Message (optional)</Label>
              <Textarea className="mt-2" rows={3} data-testid="share-email-message-input"
                value={emailForm.message}
                onChange={(e) => setEmailForm({ ...emailForm, message: e.target.value })}
                placeholder="Sharing the title documents for the loan appraisal." />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="share-email-submit-button">
                <Send size={15} className="mr-2" /> {busy ? "Sending…" : "Send link"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* CASE DIALOG */}
      <Dialog open={caseOpen} onOpenChange={setCaseOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-bold tracking-tight">
              {caseEditId ? "Edit court case" : "Add court case"}
            </DialogTitle>
            <DialogDescription>
              Court details, advocate contact, hearing dates and judgment outcome.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveCase} className="space-y-5" data-testid="case-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {inp(caseForm, setCaseForm, "Case number *", "case_number", { required: true, placeholder: "OS 214/2024" }, "case-number-input")}
              {inp(caseForm, setCaseForm, "Court name", "court_name", { placeholder: "Sr. Civil Judge, Medchal" }, "case-court-input")}
              {inp(caseForm, setCaseForm, "Case type", "case_type", { placeholder: "Title suit / Injunction" }, "case-type-input")}
              <div>
                <Label className="label-tiny">Our role</Label>
                <Select value={caseForm.our_role} onValueChange={(v) => setCaseForm({ ...caseForm, our_role: v })}>
                  <SelectTrigger className="mt-2" data-testid="case-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Petitioner", "Respondent", "Plaintiff", "Defendant", "Third party"].map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {inp(caseForm, setCaseForm, "Opposing party", "opposing_party", {}, "case-opposing-input")}
              <div>
                <Label className="label-tiny">Status</Label>
                <Select value={caseForm.status} onValueChange={(v) => setCaseForm({ ...caseForm, status: v })}>
                  <SelectTrigger className="mt-2" data-testid="case-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Ongoing", "Stayed", "Reserved for judgment", "Appeal filed", "Closed"].map((s) => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {inp(caseForm, setCaseForm, "Filed date", "filed_date", { type: "date" }, "case-filed-input")}
              {inp(caseForm, setCaseForm, "Next hearing date", "next_hearing_date", { type: "date" }, "case-hearing-input")}
            </div>

            <div className="rounded-xl border border-border p-4">
              <p className="label-tiny">Advocate details</p>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {inp(caseForm, setCaseForm, "Advocate name", "advocate_name", {}, "case-advocate-input")}
                {inp(caseForm, setCaseForm, "Firm / Chamber", "advocate_firm", {}, "case-firm-input")}
                {inp(caseForm, setCaseForm, "Phone", "advocate_phone", {}, "case-advocate-phone-input")}
                {inp(caseForm, setCaseForm, "Email", "advocate_email", { type: "email" }, "case-advocate-email-input")}
                {inp(caseForm, setCaseForm, "Fees agreed (₹)", "advocate_fees", { type: "number", step: "any" }, "case-fees-input")}
              </div>
            </div>

            <div className="rounded-xl border border-border p-4">
              <div className="flex items-center justify-between">
                <p className="label-tiny">Hearing history</p>
                <Button type="button" variant="outline" size="sm" className="rounded-lg" data-testid="add-hearing-row"
                  onClick={() => setCaseForm({ ...caseForm, hearings: [...caseForm.hearings, { hearing_date: "", purpose: "", outcome: "" }] })}>
                  <Plus size={13} className="mr-1.5" /> Add hearing
                </Button>
              </div>
              <div className="mt-4 space-y-3">
                {caseForm.hearings.length === 0 && (
                  <p className="text-xs text-muted-foreground">No hearings logged yet.</p>
                )}
                {caseForm.hearings.map((h, i) => (
                  <div key={i} className="grid grid-cols-1 sm:grid-cols-[150px_1fr_1fr_auto] gap-2 items-end">
                    <Input type="date" value={h.hearing_date} data-testid={`hearing-date-${i}`}
                      onChange={(e) => {
                        const hs = [...caseForm.hearings]; hs[i] = { ...h, hearing_date: e.target.value };
                        setCaseForm({ ...caseForm, hearings: hs });
                      }} />
                    <Input placeholder="Purpose" value={h.purpose} data-testid={`hearing-purpose-${i}`}
                      onChange={(e) => {
                        const hs = [...caseForm.hearings]; hs[i] = { ...h, purpose: e.target.value };
                        setCaseForm({ ...caseForm, hearings: hs });
                      }} />
                    <Input placeholder="Outcome" value={h.outcome} data-testid={`hearing-outcome-${i}`}
                      onChange={(e) => {
                        const hs = [...caseForm.hearings]; hs[i] = { ...h, outcome: e.target.value };
                        setCaseForm({ ...caseForm, hearings: hs });
                      }} />
                    <button type="button" data-testid={`remove-hearing-${i}`}
                      onClick={() => setCaseForm({ ...caseForm, hearings: caseForm.hearings.filter((_, x) => x !== i) })}
                      className="h-10 px-3 text-muted-foreground hover:text-destructive transition-colors duration-200">
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="label-tiny">Judgment / order summary</Label>
              <Textarea rows={3} className="mt-2" data-testid="case-judgment-input" value={caseForm.judgment_summary}
                onChange={(e) => setCaseForm({ ...caseForm, judgment_summary: e.target.value })}
                placeholder="Interim injunction granted in our favour on 12 Mar 2025…" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {inp(caseForm, setCaseForm, "Outcome in our case", "outcome", { placeholder: "In our favour / Against / Pending" }, "case-outcome-input")}
              {inp(caseForm, setCaseForm, "Notes", "notes", {}, "case-notes-input")}
            </div>

            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="case-submit-button" className="rounded-lg">
                {busy ? "Saving…" : caseEditId ? "Save case" : "Add case"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* MORTGAGE DIALOG */}
      <Dialog open={mortOpen} onOpenChange={setMortOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display font-bold tracking-tight">
              {mortEditId ? "Edit mortgage" : "Add mortgage"}
            </DialogTitle>
            <DialogDescription>Bank, loan account, amounts and dates. Attach deeds and letters after saving.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveMortgage} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="mortgage-form">
            {inp(mortForm, setMortForm, "Bank / Lender *", "bank_name", { required: true, placeholder: "State Bank of India" }, "mortgage-bank-input")}
            {inp(mortForm, setMortForm, "Branch", "branch", {}, "mortgage-branch-input")}
            {inp(mortForm, setMortForm, "Loan account no.", "loan_account_no", {}, "mortgage-account-input")}
            <div>
              <Label className="label-tiny">Mortgage type</Label>
              <Select value={mortForm.mortgage_type} onValueChange={(v) => setMortForm({ ...mortForm, mortgage_type: v })}>
                <SelectTrigger className="mt-2" data-testid="mortgage-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Equitable", "Registered", "Simple", "English", "Usufructuary"].map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {inp(mortForm, setMortForm, "Sanctioned amount (₹)", "sanctioned_amount", { type: "number", step: "any" }, "mortgage-sanctioned-input")}
            {inp(mortForm, setMortForm, "Outstanding amount (₹)", "outstanding_amount", { type: "number", step: "any" }, "mortgage-outstanding-input")}
            {inp(mortForm, setMortForm, "Interest rate (%)", "interest_rate", { type: "number", step: "any" }, "mortgage-rate-input")}
            <div>
              <Label className="label-tiny">Status</Label>
              <Select value={mortForm.status} onValueChange={(v) => setMortForm({ ...mortForm, status: v })}>
                <SelectTrigger className="mt-2" data-testid="mortgage-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Active", "Overdue", "Released", "Under process"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {inp(mortForm, setMortForm, "Mortgage date", "mortgage_date", { type: "date" }, "mortgage-date-input")}
            {inp(mortForm, setMortForm, "Maturity date", "maturity_date", { type: "date" }, "mortgage-maturity-input")}
            {inp(mortForm, setMortForm, "Release date", "release_date", { type: "date" }, "mortgage-release-input")}
            {inp(mortForm, setMortForm, "Bank contact person", "contact_person", {}, "mortgage-contact-input")}
            {inp(mortForm, setMortForm, "Contact phone", "contact_phone", {}, "mortgage-phone-input")}
            {inp(mortForm, setMortForm, "Contact email", "contact_email", { type: "email" }, "mortgage-email-input")}
            <div className="sm:col-span-2">
              <Label className="label-tiny">Notes</Label>
              <Textarea rows={3} className="mt-2" data-testid="mortgage-notes-input" value={mortForm.notes}
                onChange={(e) => setMortForm({ ...mortForm, notes: e.target.value })} />
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="submit" disabled={busy} data-testid="mortgage-submit-button" className="rounded-lg">
                {busy ? "Saving…" : mortEditId ? "Save mortgage" : "Add mortgage"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* EDIT ASSET */}
      {edit && (
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display font-bold tracking-tight">Edit asset</DialogTitle>
              <DialogDescription>
                Correct survey details, extent, location, vaastu orientation or reassign the executive.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={saveEdit} className="space-y-5" data-testid="edit-parcel-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">{inp(edit, setEdit, "Asset name", "name", { required: true }, "edit-name-input")}</div>
                <div>
                  <Label className="label-tiny">Category</Label>
                  <Select value={edit.asset_category} onValueChange={(v) => setEdit({ ...edit, asset_category: v })}>
                    <SelectTrigger className="mt-2" data-testid="edit-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {inp(edit, setEdit, "Sub type", "land_type", {}, "edit-landtype-input")}
                {inp(edit, setEdit, "Survey number", "survey_number", { required: true }, "edit-survey-input")}
                {inp(edit, setEdit, "Extent (acres)", "extent_acres", { type: "number", step: "0.01" }, "edit-extent-input")}
                {inp(edit, setEdit, "Khata / Passbook no.", "khata_number", {}, "edit-khata-input")}
                {inp(edit, setEdit, "Owner / Title holder", "owner_name", {}, "edit-owner-input")}
                {inp(edit, setEdit, "Village", "village", { required: true }, "edit-village-input")}
                {inp(edit, setEdit, "Mandal", "mandal", {}, "edit-mandal-input")}
                {inp(edit, setEdit, "District", "district", {}, "edit-district-input")}
                {inp(edit, setEdit, "State", "state", {}, "edit-state-input")}
                {inp(edit, setEdit, "Latitude", "latitude", { type: "number", step: "any" }, "edit-lat-input")}
                {inp(edit, setEdit, "Longitude", "longitude", { type: "number", step: "any" }, "edit-lng-input")}
                <div>
                  <Label className="label-tiny">Assigned executive</Label>
                  <Select value={edit.assigned_executive_id || "none"}
                    onValueChange={(v) => setEdit({ ...edit, assigned_executive_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="mt-2" data-testid="edit-executive-select">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {executives.map((ex) => (
                        <SelectItem key={ex.id} value={ex.id} data-testid={`edit-executive-option-${ex.id}`}>{ex.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-tiny">Visit frequency</Label>
                  <Select value={edit.visit_frequency} onValueChange={(v) => setEdit({ ...edit, visit_frequency: v })}>
                    <SelectTrigger className="mt-2" data-testid="edit-frequency-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="label-tiny">Status</Label>
                  <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                    <SelectTrigger className="mt-2" data-testid="edit-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Active", "Under dispute", "Sold"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {inp(edit, setEdit, "Acquisition value (₹)", "acquisition_value", { type: "number", step: "any" }, "edit-value-input")}
              </div>

              <div className="rounded-xl border border-border p-4">
                <p className="label-tiny flex items-center gap-2"><Shield size={14} className="text-primary" /> Police station jurisdiction</p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <PoliceFields state={edit} setState={setEdit} prefix="edit" />
                </div>
              </div>

              <div className="rounded-xl border border-border p-4">
                <p className="label-tiny flex items-center gap-2"><Compass size={14} className="text-primary" /> Vaastu &amp; orientation</p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {sel(edit, setEdit, "Facing direction", "vaastu_facing", DIRECTIONS, "edit-facing-select")}
                  {sel(edit, setEdit, "Plot shape", "vaastu_shape", SHAPES, "edit-shape-select")}
                  {sel(edit, setEdit, "Road position", "vaastu_road_position", DIRECTIONS, "edit-road-select")}
                  {sel(edit, setEdit, "Slope towards", "vaastu_slope", DIRECTIONS, "edit-slope-select")}
                  {sel(edit, setEdit, "Water source at", "vaastu_water_source", DIRECTIONS, "edit-water-select")}
                  {sel(edit, setEdit, "Vaastu rating", "vaastu_rating", RATINGS, "edit-vaastu-rating-select")}
                </div>
                <div className="mt-4">
                  <Label className="label-tiny">Vaastu notes</Label>
                  <Textarea rows={3} className="mt-2" data-testid="edit-vaastu-notes-input" value={edit.vaastu_notes}
                    onChange={(e) => setEdit({ ...edit, vaastu_notes: e.target.value })} />
                </div>
              </div>

              <div>
                <Label className="label-tiny">Notes</Label>
                <Textarea rows={2} className="mt-2" data-testid="edit-notes-input" value={edit.notes}
                  onChange={(e) => setEdit({ ...edit, notes: e.target.value })} />
              </div>

              <DialogFooter>
                <Button type="submit" disabled={busy} data-testid="edit-submit-button" className="rounded-lg">
                  {busy ? "Saving…" : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
